import { useMemo } from "react";
import { BufferGeometry, Float32BufferAttribute, Vector3, Color } from "three";
import { LineSegmentsGeometry, LineSegments2, LineMaterial } from "three-stdlib";
import { useThree } from "@react-three/fiber";
import type { CenterlineData } from "./useTunnelCenterline";
import {
  type ProfileAssignment,
  type TunnelProfileType,
  getProfileAtStation,
  PROFILE_2_LANE,
} from "./tunnelProfileShapes";

interface TunnelProfilesProps {
  data: CenterlineData;
  /** Distance between cross-section rings in meters */
  ringSpacing?: number;
  /** Profile assignments: which profile type at which station range */
  profileAssignments?: ProfileAssignment[];
  /** AR overlay mode — high-contrast thick rendering */
  arMode?: boolean;
}

/**
 * Renders tunnel cross-section profiles as wireframe lines in 3D.
 *
 * Each cross-section is a 2D polyline (from tunnelProfileShapes) placed
 * perpendicular to the centerline using Frenet frames.
 *
 * The 2D profile coordinate system maps to 3D as:
 *   - profile X (horizontal) -> binormal direction
 *   - profile Y (vertical up) -> normal direction (adjusted so Y=up)
 *
 * Longitudinal lines connect corresponding vertices across adjacent rings
 * that share the same profile type.
 */
export function TunnelProfiles({
  data,
  ringSpacing = 10,
  profileAssignments = [],
  arMode = false,
}: TunnelProfilesProps) {
  const { size } = useThree();

  const geometries = useMemo(() => {
    const totalLength = data.totalLength;
    const ringCount = Math.floor(totalLength / ringSpacing) + 1;
    const frameSamples = 500;

    // Compute gravity-aligned frames along the curve.
    // Instead of Frenet frames (which are unstable for near-straight tunnels),
    // we build a frame where "up" always points toward world Y.
    const curvePoints = data.curve.getSpacedPoints(frameSamples);
    const worldUp = new Vector3(0, 1, 0);

    // For each ring, determine station, profile type, and compute 3D vertices
    interface RingData {
      station: number;
      profile: TunnelProfileType;
      verts3D: Vector3[];
    }

    const rings: RingData[] = [];

    for (let i = 0; i < ringCount; i++) {
      const station = i * ringSpacing;
      const t = Math.min(station / totalLength, 1);
      const frameIdx = Math.min(Math.round(t * frameSamples), frameSamples);

      const center = curvePoints[frameIdx];
      const tangent = data.curve.getTangentAt(Math.min(t, 1)).normalize();

      // Build gravity-aligned frame: "side" is perpendicular to tangent and world-up,
      // "up" is perpendicular to tangent and side (stays close to world-up).
      let side = new Vector3().crossVectors(tangent, worldUp).normalize();
      if (side.lengthSq() < 0.001) {
        // Tangent is nearly vertical — fall back to world-X as reference
        side.crossVectors(tangent, new Vector3(1, 0, 0)).normalize();
      }
      const up = new Vector3().crossVectors(side, tangent).normalize();

      // Get the profile for this station
      const profile = getProfileAtStation(station, profileAssignments);

      // Map 2D profile points to 3D
      // Profile X -> side (horizontal), Profile Y -> up (vertical)
      const verts3D = profile.points.map((p2d) => {
        return new Vector3(
          center.x + p2d.x * side.x + p2d.y * up.x,
          center.y + p2d.x * side.y + p2d.y * up.y,
          center.z + p2d.x * side.z + p2d.y * up.z,
        );
      });

      rings.push({ station, profile, verts3D });
    }

    // Build ring line segments, grouped by profile color
    const ringVertsByColor: Record<string, number[]> = {};
    for (const ring of rings) {
      const color = ring.profile.color;
      if (!ringVertsByColor[color]) ringVertsByColor[color] = [];
      const arr = ringVertsByColor[color];
      for (let j = 0; j < ring.verts3D.length - 1; j++) {
        const a = ring.verts3D[j];
        const b = ring.verts3D[j + 1];
        arr.push(a.x, a.y, a.z, b.x, b.y, b.z);
      }
    }

    // Build longitudinal lines connecting same-index vertices on adjacent rings
    const longVerts: number[] = [];
    for (let i = 0; i < rings.length - 1; i++) {
      const rA = rings[i];
      const rB = rings[i + 1];
      const count = Math.min(rA.verts3D.length, rB.verts3D.length);
      for (let j = 0; j < count; j++) {
        const a = rA.verts3D[j];
        const b = rB.verts3D[j];
        longVerts.push(a.x, a.y, a.z, b.x, b.y, b.z);
      }
    }

    // Create standard BufferGeometry for non-AR mode
    const ringGeos: { geometry: BufferGeometry; color: string }[] = [];
    for (const [color, verts] of Object.entries(ringVertsByColor)) {
      const geo = new BufferGeometry();
      geo.setAttribute("position", new Float32BufferAttribute(verts, 3));
      ringGeos.push({ geometry: geo, color });
    }

    const longGeo = new BufferGeometry();
    longGeo.setAttribute("position", new Float32BufferAttribute(longVerts, 3));

    // Create fat LineSegments2 geometries for AR mode (supports real linewidth)
    const ringFatGeos: { geometry: LineSegmentsGeometry; color: string }[] = [];
    for (const [color, verts] of Object.entries(ringVertsByColor)) {
      const geo = new LineSegmentsGeometry();
      geo.setPositions(verts);
      ringFatGeos.push({ geometry: geo, color });
    }

    const longFatGeo = new LineSegmentsGeometry();
    longFatGeo.setPositions(longVerts);

    return { ringGeos, longitudinalGeometry: longGeo, ringFatGeos, longitudinalFatGeometry: longFatGeo };
  }, [data, ringSpacing, profileAssignments]);

  // AR mode: fat lines with LineMaterial (screen-space pixel width)
  const resolution = useMemo(() => [size.width, size.height] as [number, number], [size]);

  const ringFatMaterials = useMemo(() => {
    if (!arMode) return [];
    return geometries.ringFatGeos.map(() => {
      const mat = new LineMaterial({
        color: 0xffffff,
        linewidth: 3,
        transparent: true,
        opacity: 1.0,
        depthTest: true,
        resolution: { x: resolution[0], y: resolution[1] } as any,
      });
      (mat as any).resolution.set(resolution[0], resolution[1]);
      return mat;
    });
  }, [arMode, geometries.ringFatGeos, resolution]);

  const longFatMaterial = useMemo(() => {
    if (!arMode) return null;
    const mat = new LineMaterial({
      color: 0xffffff,
      linewidth: 2,
      transparent: true,
      opacity: 0.6,
      depthTest: true,
      resolution: { x: resolution[0], y: resolution[1] } as any,
    });
    (mat as any).resolution.set(resolution[0], resolution[1]);
    return mat;
  }, [arMode, resolution]);

  if (arMode) {
    return (
      <group>
        {geometries.ringFatGeos.map((rg, i) => (
          <primitive key={`ring-fat-${i}`} object={new LineSegments2(rg.geometry, ringFatMaterials[i])} />
        ))}
        {longFatMaterial && (
          <primitive object={new LineSegments2(geometries.longitudinalFatGeometry, longFatMaterial)} />
        )}
      </group>
    );
  }

  return (
    <group>
      {geometries.ringGeos.map((rg, i) => (
        <lineSegments key={`ring-${i}`} geometry={rg.geometry}>
          <lineBasicMaterial color={rg.color} transparent opacity={0.7} />
        </lineSegments>
      ))}
      <lineSegments geometry={geometries.longitudinalGeometry}>
        <lineBasicMaterial color="#666666" transparent opacity={0.25} />
      </lineSegments>
    </group>
  );
}
